// CountdownCard.tsx
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { Card, CardContent, Grid2, Typography } from "@mui/material";
import { StandardButton } from "../../../_components/button/primary.button";
import { catalogueItemService } from "../../../_services/catalogueItemService.service";
import { AuctionCatalogueStatusEnum } from "../../../_common/enum/auctionCatalogueStatus.enum";
import { ActualAuctionStatusEnum } from "../../../_common/enum/actualAuctionStatus";
import { auctionService } from "../../../_services/auctionService.service";
import { AuctionStatusEnum } from "../../../_common/enum/auctionStatus.enum";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { ValidatorForm } from "react-material-ui-form-validator";
import { ConfirmDialog } from "../../../_components/notification/confirmDialog.component";
import { catalogueService } from "../../../_services/catalogueService.service";
import { catalogueUserService } from "../../../_services/catalogueUser.service";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";

// utils
const timeToSeconds = (mmss: string) => {
    if (!mmss) return 0;
    const parts = mmss.trim().split(":");
    if (parts.length === 1) return Number(parts[0]) || 0; // "45" -> 45s
    const [mStr = "0", sStr = "0"] = parts.slice(-2);     // ignore hours if present
    const m = Number(mStr) || 0;
    const s = Number(sStr) || 0;
    return m * 60 + s;
};

// seconds → "MM:SS"
const secondsToTime = (sec: number) => {
    const m = Math.floor(Math.max(0, sec) / 60);
    const s = Math.floor(Math.max(0, sec) % 60);
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
};

export function CountdownControls(params: {
    catalogueItem?: any,
    auctionId?: number,
    actualAuctionStatusId: ActualAuctionStatusEnum,
    onlineUsers?: string[],
    defaultSecondsLeft: number,
    onRestart: () => void,
}) {
    const isPaused = (params?.actualAuctionStatusId & ActualAuctionStatusEnum.IsPaused) != 0;

    const [modalds, setModals] = useState({
        startAuction: false,
        startAuctionHasOfflineUsers: false,
        startAuctionOfflineUserCount: 0,
    });
    const [checkingStartEligibility, setCheckingStartEligibility] = useState(false);

    const handleStart = () => {
        if (!params.auctionId) return;

        auctionService
            .setStatus(params?.auctionId, AuctionStatusEnum.Active);
    };

    const handleClosingShortly = () => {
        if (!params.catalogueItem?.id) return;

        catalogueItemService
            .setStatus(params.catalogueItem?.id, AuctionCatalogueStatusEnum.ClosingShortly);
    };

    const handlePause = () => {
        if (!params.auctionId) return;

        auctionService
            .setStatus(params?.auctionId, AuctionStatusEnum.Paused);
    };

    const handleEnd = () => {
        if (!params.catalogueItem?.id) return;

        catalogueItemService
            .setStatus(params.catalogueItem?.id, AuctionCatalogueStatusEnum.BidAcceptance);
    };

    const handleStartAuction = () => {
        if (!params.auctionId) return;

        setModals(m => ({ ...m, startAuction: false }));

        auctionService
            .activate(params.auctionId);
    };

    const handleStartAuctionClick = async () => {
        if (!params.auctionId) return;

        setCheckingStartEligibility(true);
        try {
            const catalogues = await catalogueService.getAllAuction(params.auctionId);
            const catalogueIds = (Array.isArray(catalogues) ? catalogues : [])
                .map((c: any) => Number(c?.id))
                .filter((id: number) => id > 0);

            const usersPerCatalogue = await Promise.all(
                catalogueIds.map((catalogueId) =>
                    catalogueUserService.getAll(catalogueId).catch(() => []),
                ),
            );

            const registeredUserIds = new Set<string>();
            usersPerCatalogue.forEach((users: any) => {
                if (!Array.isArray(users)) return;
                users.forEach((user: any) => {
                    const userId = String(user?.userId ?? "").trim();
                    if (userId) registeredUserIds.add(userId);
                });
            });

            const onlineUsers = new Set((params.onlineUsers ?? []).map((u) => String(u)));
            const offlineUserCount = Array.from(registeredUserIds).filter(
                (id) => !onlineUsers.has(id),
            ).length;

            setModals({
                startAuction: true,
                startAuctionHasOfflineUsers: offlineUserCount > 0,
                startAuctionOfflineUserCount: offlineUserCount,
            });
        } catch (err) {
            handleErrorResponse(err);
            setModals({
                startAuction: true,
                startAuctionHasOfflineUsers: false,
                startAuctionOfflineUserCount: 0,
            });
        } finally {
            setCheckingStartEligibility(false);
        }
    };

    return (
        <Grid2 container spacing={1}>
            {(!!params.auctionId && !!params.catalogueItem) &&
                <>
                    <Grid2 size={{ xs: 2.4 }}>
                        <StandardButton
                            icon="access_time"
                            variant="success"
                            disabled={!isPaused}
                            onClick={handleStart}>
                            {params.actualAuctionStatusId === ActualAuctionStatusEnum.Pending ? "Open bidding" : "Start Timer"}
                        </StandardButton>
                    </Grid2>

                    <Grid2 size={{ xs: 2.4 }}>
                        <StandardButton
                            icon="priority_high"
                            variant="error"
                            disabled={params.actualAuctionStatusId !== ActualAuctionStatusEnum.Active}
                            onClick={handleClosingShortly}>
                            Closing shortly
                        </StandardButton>
                    </Grid2>

                    <Grid2 size={{ xs: 2.4 }}>
                        <StandardButton
                            icon="restart_alt"
                            variant="info"
                            disabled={!(params.actualAuctionStatusId === ActualAuctionStatusEnum.Active || params.actualAuctionStatusId === ActualAuctionStatusEnum.ReOffered || params.actualAuctionStatusId === ActualAuctionStatusEnum.Paused || params.actualAuctionStatusId === ActualAuctionStatusEnum.UserDisconnected || params.actualAuctionStatusId === ActualAuctionStatusEnum.Pending)}
                            onClick={params.onRestart}>
                            Restart Timer [{params.defaultSecondsLeft}s]
                        </StandardButton>
                    </Grid2>

                    <Grid2 size={{ xs: 2.4 }}>
                        <StandardButton
                            icon="block"
                            variant="warning"
                            disabled={!(params.actualAuctionStatusId === ActualAuctionStatusEnum.Active || params.actualAuctionStatusId === ActualAuctionStatusEnum.ReOffered || params.actualAuctionStatusId === ActualAuctionStatusEnum.ClosingShortly)}
                            onClick={handlePause}>
                            Pause Timer
                        </StandardButton>
                    </Grid2>

                    <Grid2 size={{ xs: 2.4 }}>
                        <StandardButton
                            icon="check"
                            variant="primary"
                            disabled={!params.catalogueItem || (params.actualAuctionStatusId !== ActualAuctionStatusEnum.Active && params.actualAuctionStatusId !== ActualAuctionStatusEnum.ReOffered && params.actualAuctionStatusId !== ActualAuctionStatusEnum.ClosingShortly)}
                            onClick={handleEnd}>
                            Complete Item
                        </StandardButton>
                    </Grid2>
                </>}

            {(!!params.auctionId && (params.actualAuctionStatusId === ActualAuctionStatusEnum.Loading || params.actualAuctionStatusId === ActualAuctionStatusEnum.NotStarted)) &&
                <Grid2 size={{ xs: 12 }}>
                    <StandardButton
                        icon="check"
                        variant="success"
                        loading={checkingStartEligibility}
                        disabled={checkingStartEligibility}
                        onClick={handleStartAuctionClick}>
                        Start Auction
                    </StandardButton>
                </Grid2>
            }

            <ConfirmDialog
                open={modalds.startAuction}
                title={
                    modalds.startAuctionHasOfflineUsers
                        ? `At least one registered user is offline (${modalds.startAuctionOfflineUserCount} offline). Are you sure you want to start the auction?`
                        : "Are you sure you ready to start the auction?"
                }
                onClose={() => {
                    setModals(m => ({ ...m, startAuction: false }))
                }}
                onConfirm={handleStartAuction}
            />
        </Grid2>
    );
}

export default function CountdownCard(params: {
    actualAuctionStatusId: ActualAuctionStatusEnum,
    secondsLeft: number,
    setSecondsLeft: Dispatch<SetStateAction<number>>,
    defaultSecondsLeft: number,
    setDefaultSecondsLeft: Dispatch<SetStateAction<number>>,
}) {
    const { secondsLeft, setSecondsLeft, defaultSecondsLeft, setDefaultSecondsLeft } = params;

    const intervalRef = useRef<number | null>(null);

    const isRunning = params?.actualAuctionStatusId === ActualAuctionStatusEnum.Active || params?.actualAuctionStatusId === ActualAuctionStatusEnum.ReOffered || params?.actualAuctionStatusId === ActualAuctionStatusEnum.ClosingShortly;

    useEffect(() => {
        if (!isRunning || secondsLeft <= 0) {
            if (intervalRef.current !== null) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
            return;
        }

        intervalRef.current = window.setInterval(() => {
            setSecondsLeft((prev) => {
                if (prev <= 1) {
                    if (intervalRef.current !== null) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (intervalRef.current !== null) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [isRunning, secondsLeft, setSecondsLeft]);

    useEffect(() => {
        if (params?.actualAuctionStatusId == ActualAuctionStatusEnum.Pending || params?.actualAuctionStatusId == ActualAuctionStatusEnum.BidAcceptance) {
            setSecondsLeft(defaultSecondsLeft);
        }
    }, [params?.actualAuctionStatusId]);

    return (
        <Card elevation={1} sx={{ width: "100%", textAlign: "center" }}>
            <CardContent>
                <ValidatorForm
                    onSubmit={() => { }}
                    noValidate>
                    <Typography
                        variant="overline"
                        sx={{ letterSpacing: 1 }}
                        aria-live="polite"
                        aria-atomic="true"
                    >
                        Countdown
                    </Typography>
                    <Typography
                        variant="h3"
                        component="div"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                    >
                        <StandardTextValidator
                            sx={{
                                width: "225px",
                                '& .MuiInputBase-input': {
                                    fontSize: 48,
                                    lineHeight: 1.2,
                                    padding: '10px 12px'
                                }
                            }}
                            name="bid"
                            fullWidth
                            label=""
                            type="time"
                            value={secondsToTime(secondsLeft)}
                            onChange={(e: any) => {
                                setDefaultSecondsLeft(timeToSeconds(e.target.value))
                                setSecondsLeft(timeToSeconds(e.target.value))
                            }}
                        />
                    </Typography>
                </ValidatorForm>
            </CardContent>
        </Card>
    );
}
